<template>
  <div class="bread-crumbs" style="height: 80px;" v-if="post">
    <nav aria-label="Breadcrumb">
      <template v-for="(crumb, i) in breadcrumbs" :key="i">
        <div class="crumb-box" :class="{ current: crumb.isLast }">{{ crumb.text }}</div>
        <div v-if="i < breadcrumbs.length - 1" class="crumb-sep">
          <i class="fas fa-chevron-right crumb-icon" aria-hidden="true"></i>
        </div>
      </template>
    </nav>
  </div>
  <div style="height: 80px;" v-else> </div>
  <div class="row screen-height">
    <div class="col-8">
      <h1 class="post-title" v-if="post">{{ post.title.rendered }}</h1>
      <hr class="divider" v-if="post"/>
      <div class="post-detail" v-if="post">
        <div class="post-meta">
          <span>{{ formattedDate }}</span>
          <span v-if="post.author_name"> | By {{ post.author_name }}</span>
        </div>
        <div class="post-content" v-html="processedContent"></div>
      </div>
      <div v-else-if="loading" class="loading">Loading...</div>
      <div v-else class="error">Post not found.</div>
    </div>
    <div class="col-4">
      <UpdatesBlock class="flex-h-100" />
    </div>
  </div>
</template>

<script>
import UpdatesBlock from '../components/UpdatesBlock.vue';
import { safeFetch } from '@/utils/fetch';

const POST_TYPE_ENDPOINTS = {
  post: '/wp-json/wp/v2/posts/',
  announcement: '/wp-json/wp/v2/announcement/',
  event: '/wp-json/wp/v2/events/',
  goal: '/wp-json/wp/v2/goal/',
  process: '/wp-json/wp/v2/process/',
  training: '/wp-json/wp/v2/training/',
};

export default {
  name: "PostDetail",
  components: { UpdatesBlock },
  data() {
    return {
      post: null,
      loading: true,
      postType: 'post',
    };
  },
  computed: {
    formattedDate() {
      if (!this.post) return "";
      const date = new Date(this.post.date);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },
    processedContent() {
      if (!this.post || !this.post.content || !this.post.content.rendered) return "";
      const html = this.post.content.rendered;
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const legends = doc.querySelectorAll('.gform_wrapper legend');
      legends.forEach(legend => {
        const label = document.createElement('label');
        if (legend.id) label.id = legend.id;
        if (legend.className) label.className = legend.className;
        label.innerHTML = legend.innerHTML;
        legend.parentNode.replaceChild(label, legend);
      });
      return doc.body.innerHTML;
    },
    breadcrumbs() {
      // Build breadcrumb segments from the current route path.
      // Do not include "Home" and render as plain text only.
      const rawSegments = (this.$route && this.$route.path) ? this.$route.path.split('/').filter(Boolean) : [];
      const segments = [];
      let pathSoFar = '';

      rawSegments.forEach((seg, idx) => {
        pathSoFar += '/' + seg;
        const isLast = idx === rawSegments.length - 1;
        let text = decodeURIComponent(seg).replace(/-/g, ' ');
        if (isLast && this.post && this.post.title && this.post.title.rendered) {
          text = this.post.title.rendered;
        } else {
          text = text.replace(/\b\w/g, c => c.toUpperCase());
        }
        segments.push({
          text,
          path: pathSoFar,
          isLast
        });
      });

      return segments;
    }
  },
  async created() {
    await this.fetchPost();
  },
  watch: {
    '$route': {
      immediate: false,
      handler() {
        this.fetchPost();
      }
    }
  },
  methods: {
    async fetchPost() {
      this.loading = true;
      this.post = null;
      const { slug, type } = this.$route.params;
      this.postType = type || 'post';
      const endpoint = `${POST_TYPE_ENDPOINTS[this.postType] || POST_TYPE_ENDPOINTS.post}`;
      try {
        const token = localStorage.getItem('authToken');
        const res = await safeFetch(`${endpoint}?slug=${slug}&_embed`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (!res.ok) throw new Error("Not found");
        const posts = await res.json();
        const post = posts[0]; // API returns an array for slug queries
        if (post && post._embedded && post._embedded.author && post._embedded.author[0]) {
          post.author_name = post._embedded.author[0].name;
        }
        this.post = post || null;
      } catch (e) {
        this.post = null;
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.post-detail {
  text-align: left;
}
.post-title {
  margin-bottom: 0.5rem;
}
.post-meta {
  color: #888;
  font-size: 0.95em;
  margin-bottom: 1.5rem;
}
.post-content {
  line-height: 1.7;
}
.loading,
.error {
  text-align: center;
  margin: 2rem 0;
  color: #666;
}

/* Divider between title and content */
.divider {
  border: none;
  height: 1px;
  background-color: #94918B;
  margin: 0 0 1.5rem;
}

/* simple breadcrumb styling */
.bread-crumbs {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
  color: #555;
}
.bread-crumbs .crumb .current {
  font-weight: 600;
}

/* breadcrumb boxes */
.bread-crumbs nav {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}
.crumb-box {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: #f5f5f5;
  color: #94918B;
  font-size: 0.95rem;
  font-weight: 500;
  white-space: nowrap;
}
.crumb-box.current {
  background: #e9e9e9;
  color: #025780;
}
.crumb-sep {
  display: inline-flex;
  align-items: center;
  padding: 0 4px;
}

/* Font Awesome chevron styling via css class */
.crumb-icon {
  font-size: 0.9rem;
  color: #777;
  line-height: 1;
  display: inline-block;
}

/* small horizontal spacing for the icon */
.crumb-sep .crumb-icon {
  margin: 0 2px;
}
</style>